import { Injectable } from "@angular/core";
import { MCGeneratorFieldsService, MCResponse, MC_RESPONSE_STATUS_CODE, MCSectionSchema, MCFormisForm, MCDialog, MCDialogButton, MCDialogFooter, MCDialogHeader, MCUnionRequestHttpWrapperService } from "@maslul/common-services";
import { FieldsMapperService } from "../../fields/mapper";
import { ProductsStoreService } from "../../store/products";
import { Guid } from "guid-typescript";
import { map, Observable, of } from "rxjs";
import { NavigationService } from "../../shared/navigation.service";
import { RequestApprovalTypeDetailsStore } from "../../store/approval-type-details-store";
import { FormisFormStoreData } from "../../../models/store/formis-form-store-data";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { ApprovalTypesService } from "../../approval-type/approval-type";
import { TABS } from "../../../enum/request/TABS";
import cloneDeep from "clone-deep";
import { ProgressService } from "../../shared/progress";

@Injectable({ providedIn: 'root' })
export class ProductsService {

    constructor(private fieldsMapperService: FieldsMapperService,
        private productsStoreService: ProductsStoreService,
        private generatorFieldsService: MCGeneratorFieldsService<MCUnionRequestHttpWrapperService<any>>,
        private requestApprovalTypeDetailsStore: RequestApprovalTypeDetailsStore,
        private navigationService: NavigationService,
        private approvalTypesService: ApprovalTypesService,
        private progressService: ProgressService
    ) {
    }

    getFields(guid: string, formisForm: MCFormisForm): Observable<Array<FormlyFieldConfig> | undefined> {
        try {
            const approvalTypeId: string = this.requestApprovalTypeDetailsStore.getSnapshot().approvalTypeId;
            if (!approvalTypeId) return of(undefined);

            const productsList = this.productsStoreService.getSnapshot().products;
            const guidString = guid.toString();

            // בדיקה האם המוצר כבר קיים בסטור
            if (this.isStoredProduct(productsList, guidString)) {
                const storedData = productsList?.get(guidString);

                // הפתרון: יצירת אובייקטים חדשים (Fresh Instances) מהסכימה הגולמית
                if (storedData && storedData.schemas) {
                    console.log('Generating fresh fields from stored schemas for guid:', guidString);

                    const freshFields = this.fieldsMapperService.generateSchema(storedData.schemas, formisForm);

                    // עדכון אופציונלי בסטור כדי שהשדות יהיו מסונכרנים למופע החדש
                    this.productsStoreService.updateProductFields(guidString, freshFields);

                    return of(freshFields);
                }
            }

            // אם המוצר לא בסטור, נטען מהשרת (בדיוק כמו שכתבת קודם)
            return this.generatorFieldsService.getFieldsItems([parseInt(approvalTypeId)], [])
                .pipe(map((res: MCResponse) => {
                    if (res && res.code === MC_RESPONSE_STATUS_CODE.SUCCESS) {
                        const schemas: Array<MCSectionSchema> = res.data as Array<MCSectionSchema>;

                        // יצירת השדות בפעם הראשונה
                        const generatedSchema: Array<FormlyFieldConfig> = this.fieldsMapperService.generateSchema(schemas, formisForm);

                        // שמירה בסטור - חשוב מאוד לוודא שה-schemas נשמרים כאן!
                        this.productsStoreService.addProduct({
                            formisForm: new MCFormisForm(),
                            formisFormModel: {},
                            formisFormFields: generatedSchema,
                            schemas: schemas // המקור שממנו נבנה מחדש בהמשך
                        }, guidString);

                        return generatedSchema;
                    } else {
                        throw new Error(res.data);
                    }
                }));
        }
        catch (ex) {
            console.error('Error in getFields:', ex);
            return of(undefined);
        }
    }
    private isStoredProduct(products: Map<string, FormisFormStoreData> | undefined, guidString: string): boolean {
        return !!products?.has(guidString)
    }

    createNewProductAndNavigate(): void {
        const newGuid: Guid = Guid.create();
        this.navigationService.navigationWithParams(['/product', newGuid.toString()]);
    }

    navigateToNextProduct(): void {
        const nextProduct = this.productsStoreService.getNextProductGuid();
        if (!nextProduct) {
            this.navigationService.navigation('request-details');
        } else {
            this.navigationService.navigationWithParams(['/product', nextProduct.toString()]);
        }
    }

    navigateToPreviousProduct(): void {
        const previousProduct = this.productsStoreService.getPreviousProductGuid();
        if (!previousProduct) {
            if (this.requestApprovalTypeDetailsStore.getSnapshot().approvalTabs.includes(TABS.GENERAL_DETAILS)) {
                this.navigationService.navigation('general-details')
            }
            else {
                this.navigationService.navigation('applicant-details')

            }
        } else {
            this.navigationService.navigationWithParams(['/product', previousProduct.toString()]);
        }
    }

    nextToProductTab(): void {
        const firstGuid = this.productsStoreService.getFirstGuid();
        if (!firstGuid) {
            const newGuid = Guid.create();
            this.navigationService.navigationWithParams(['/product', newGuid.toString()]);
        } else {
            this.navigationService.navigationWithParams(['/product', firstGuid.toString()]);
        }
    }

    prevToProductTab(): void {
        const lastGuid = this.productsStoreService.getLastGuid();
        if (!lastGuid) {
            console.log("no last guid");
        }
        else {
            this.navigationService.navigationWithParams(['/product', lastGuid]);
        }
    }

    getActiveProduct(): FormisFormStoreData | undefined {
        return this.productsStoreService.getSnapshot().products?.get(
            this.productsStoreService.getSnapshot().activeProduct || ""
        );
    }

    isMaxProductReached(): boolean {
        const approvalTypeId: string = this.requestApprovalTypeDetailsStore.getSnapshot().approvalTypeId;
        return (this.productsStoreService.getSnapshot().products?.size || 0) >=
            this.approvalTypesService.maxCountRowsToApprovalTypeId[parseInt(approvalTypeId)];
    }

    maxProductsInfoMessage(): string {
        const approvalTypeId: string = this.requestApprovalTypeDetailsStore.getSnapshot().approvalTypeId;
        const maxProducts = this.approvalTypesService.maxCountRowsToApprovalTypeId[parseInt(approvalTypeId)];
        const message = `ניתן להגיש בקשה לאישור ל ${maxProducts} מוצרים `;
        return message;
    }

    isLastProduct(guid: string): boolean {
        const productsList = this.productsStoreService.getSnapshot().products;
        if (!productsList || productsList.size === 0) {
            return false;
        }

        const lastGuid = this.productsStoreService.getLastGuid();
        return lastGuid === guid;
    }

    deleteProduct(guid: string) {
        const productsList = cloneDeep(this.productsStoreService.getSnapshot().products);
        const isSuccess = productsList?.delete(guid);
        if (isSuccess) {
            this.productsStoreService.setProductsMap(productsList);

        }
    }

    saveProduct(productGuid: string, formisForm: MCFormisForm): void {
        if (!productGuid) {
            console.warn('Cannot save product, product or GUID is missing.');
            return;
        }
        this.productsStoreService.updateProductModel(productGuid, formisForm.model);
        this.productsStoreService.updateProductFields(productGuid, formisForm.fields);
    }

    showDialogBeforeDeleteProduct(dialogProperties: MCDialog, dialogText: any, productGuid: string) {
        dialogProperties.visible = true;


        dialogProperties.header = new MCDialogHeader();
        dialogProperties.header.closeButton = new MCDialogButton();
        dialogProperties.header.closeButton.label = "X";
        dialogProperties.header.closeButton.event = () => { dialogProperties.visible = false };

        dialogProperties.footer = new MCDialogFooter();
        dialogProperties.footer.button = new MCDialogButton();
        dialogProperties.footer.button.label = dialogText.okButton;
        dialogProperties.footer.button.event = () => {
            this.navigateToPreviousProduct();
            this.deleteProduct(productGuid);
            this.progressService.removeProductTab(productGuid);
            dialogProperties.visible = false
        };


        dialogProperties.footer.secondButton = new MCDialogButton();
        dialogProperties.footer.secondButton.label = dialogText.cancelButton;
        dialogProperties.footer.secondButton.class = "mi-white-button";
        dialogProperties.footer.secondButton.event = () => { dialogProperties.visible = false };
    }
}